import type { AccountInfo } from '@azure/msal-browser';

import { msalInstance } from './msalConfig';
import { setSecureValue } from '@helpers';
import { B2C_ENV_MAP } from '@api';

 // Establishes a per-tab MSAL session for the given account.
 // Sets the active account in MSAL's in-memory state and stores all
 // session values in sessionStorage (routed via WEB_SESSION_KEYS).
 // Must be called on every login, redirect, and account switch to keep the per-tab session consistent.

// Inverse of setMsalSession: clears the per-tab session keys and MSAL's active-account
// pointer so the app falls back to the login screen on next render and the next
// acquireTokenSilent won't retry against a stale identity. Does not remove accounts
// from MSAL's localStorage cache (they remain selectable) or touch Redux state
// (use unlogin() for a full teardown).
export const clearMsalSession = () => {
  msalInstance.setActiveAccount(null);
  setSecureValue('_sf_active_account_id', '');
  setSecureValue('token', '');
};

// Picks the account to silently sign into on a fresh tab when there is no per-tab
// session (`_sf_active_account_id`) yet. Prefers MSAL's active account; falls back
// to the only cached account when there's exactly one. Returns null when multiple
// accounts are cached without an explicit active choice — the caller must show a
// chooser in that case rather than guessing.
//
// This is the only place in the app that selects an account on the user's behalf without interaction.
export const pickAutoSelectAccount = (): AccountInfo | null => {
  const active = msalInstance.getActiveAccount();
  if (active) return active;
  const all = msalInstance.getAllAccounts();
  return all.length === 1 ? all[0] : null;
};

export const setMsalSession = (account: AccountInfo, accessToken: string) => {
  msalInstance.setActiveAccount(account);
  setSecureValue('_sf_active_account_id', account.homeAccountId);
  setSecureValue('token', accessToken);
  setSecureValue('email', account.username);

  // Note: redux-persist scope (setPersistUserEmail) is flipped inside
  // reinitializeAppState, AFTER dispatch(clear()), so stale state from the
  // previous account cannot be written to the new account's persist bucket.

  // Extract user info and environment from B2C JWT claims.
  // This provides immediate values before generateTokenInfo() runs.
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  if (claims) {
    if (claims.name && typeof claims.name === 'string') {
      setSecureValue('name', claims.name);
    }
    if (claims.given_name && typeof claims.given_name === 'string') {
      setSecureValue('firstName', claims.given_name);
    }
    if (claims.family_name && typeof claims.family_name === 'string') {
      setSecureValue('lastName', claims.family_name);
    }

    const envClaim = claims.extension_Environment;
    if (envClaim && typeof envClaim === 'string') {
      const mappedEnv = B2C_ENV_MAP[envClaim];
      if (mappedEnv) {
        setSecureValue('env', mappedEnv);
      }
    }
  }
};
